import React, { useEffect, useState } from "react";
import { Eye, LogOut } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

export function ClientViewBanner() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user?.client_view) return undefined;
    let live = true;
    const verify = async () => {
      try {
        const { data } = await api.get("/client-view/status");
        if (live && !data.active) {
          toast.message("Employer view ended. You are back in your administrator workspace.");
          await refresh();
          window.location.assign("/admin");
        }
      } catch {
        // The normal auth interceptor handles a true administrator session failure.
      }
    };
    verify();
    const timer = window.setInterval(verify, 60000);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [refresh, user?.client_view]);

  if (!user?.client_view) return null;

  const stop = async () => {
    setBusy(true);
    try {
      await api.post("/admin/client-view/stop");
      await refresh();
      window.location.assign("/admin");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div
      className="w-full flex flex-wrap items-center justify-between gap-3 px-6 py-2.5 text-sm"
      style={{ background: "#E8871E", color: "#1A1A17" }}
      data-testid="client-view-banner"
    >
      <span className="flex items-center gap-2 font-medium">
        <Eye size={15} /> Employer view — {user.company} · started by {user.viewed_by}
        {user.virtual && " (no portal user yet, so this view is read-only)"}
      </span>
      <button
        onClick={stop}
        disabled={busy}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border"
        style={{ borderColor: "#1A1A17" }}
        data-testid="client-view-stop"
      >
        <LogOut size={13} /> {busy ? "Returning…" : "Exit client view"}
      </button>
    </div>
  );
}

export function ClientViewButton({ company, className = "" }) {
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    try {
      await api.post("/admin/client-view/start", { company });
      window.location.assign("/dashboard");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <button
      onClick={start}
      disabled={busy}
      className={`btn-ghost flex items-center gap-1.5 text-xs ${className}`}
      data-testid={`client-view-start-${company}`}
    >
      <Eye size={13} /> {busy ? "Opening…" : "View As Employer"}
    </button>
  );
}
